import { uiSurface } from "shared/ui/uiSurface"
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from "@mui/material"
import { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

const legendColors = [
  "#f6c23e",
  "#4e73df",
  "#1cc88a",
  "#36b9cc",
  "#9b59b6",
  "#e74a3b",
  "#6f42c1",
]

export const DashboardMapCustomersWithRecurringOrders: FC = () => {
  const { t } = useTranslation()
  const [mapType, setMapType] = useState<"roadmap" | "satellite">("roadmap")

  const mapSrc = useMemo(() => {
    const base = "https://maps.google.com/maps"
    const q = "45.4042,-71.8929"
    const type = mapType === "satellite" ? "k" : "m"
    return `${base}?q=${encodeURIComponent(q)}&z=12&t=${type}&output=embed`
  }, [mapType])

  const days = useMemo(
    () => [
      t("dashboard.day.sunday"),
      t("dashboard.day.monday"),
      t("dashboard.day.tuesday"),
      t("dashboard.day.wednesday"),
      t("dashboard.day.thursday"),
      t("dashboard.day.friday"),
      t("dashboard.day.saturday"),
    ],
    [t],
  )

  return (
    <Card
      {...uiSurface("UI-DLV2-076")}
      data-testid="dashboard-recurring-orders-card"
      variant="outlined"
      sx={{ borderRadius: 2 }}
    >
      <CardContent sx={{ p: 2.25 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          {t("dashboard.customersRecurringOrders")}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
          <Button
            data-testid="dashboard-recurring-orders-map-btn"
            variant={mapType === "roadmap" ? "contained" : "outlined"}
            size="small"
            onClick={() => setMapType("roadmap")}
          >
            {t("dashboard.map")}
          </Button>
          <Button
            data-testid="dashboard-recurring-orders-satellite-btn"
            variant={mapType === "satellite" ? "contained" : "outlined"}
            size="small"
            onClick={() => setMapType("satellite")}
          >
            {t("dashboard.satellite")}
          </Button>
        </Stack>

        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            height: 420,
            overflow: "hidden",
            bgcolor: "grey.50",
          }}
        >
          <Box
            component="iframe"
            src={mapSrc}
            width="100%"
            height="100%"
            sx={{ border: 0 }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title={t("dashboard.customersRecurringOrders")}
          />
        </Box>

        <Stack
          direction="row"
          spacing={2}
          sx={{
            mt: 2,
            flexWrap: "wrap",
            p: 1.25,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          {days.map((day, index) => (
            <Stack key={day} direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  bgcolor: legendColors[index],
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {day}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </Card>
  )
}
